import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Table, Tag, Button, Popconfirm, Typography, message, Tooltip, Space, Select, Input, Segmented, Empty } from "antd";
import { LaptopOutlined, MobileOutlined, SafetyCertificateOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, CheckOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";

const { Text } = Typography;

const brand = { primary: "#242166", soft: "#f5f6fa" };

const hasHAC113 = (LOGGED_USER?.profil?.habilitations || [])
  .some((h) => h?.codeHabilitation === "HAC113");
const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
const ownOrgCode = LOGGED_USER?.profil?.organisme?.code || null;

// Oversight admin des appareils (sécurité par appareil). Pool voit tout ; compagnie voit les siens.
const AdminAppareils = () => {
  const [mode, setMode] = useState("organisme"); // 'organisme' | 'username'
  const [organismeCode, setOrganismeCode] = useState(ownOrgCode);
  const [organismes, setOrganismes] = useState([]);
  const [usernameSearch, setUsernameSearch] = useState("");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtre, setFiltre] = useState("");

  // Liste des compagnies pour le sélecteur (Pool uniquement).
  useEffect(() => {
    if (!isPool) return;
    axiosInstance
      .get("/api/organisme/all/assurances")
      .then((res) => setOrganismes(Array.isArray(res.data) ? res.data : []))
      .catch(() => { /* silencieux */ });
  }, []);

  const chargerOrganisme = useCallback((code) => {
    if (!code) return;
    setLoading(true);
    axiosInstance
      .get(`/api/device/organisme/${code}`)
      .then((res) => setDevices(Array.isArray(res.data) ? res.data : []))
      .catch((e) => message.error(e?.response?.status === 403
        ? "Accès non autorisé pour cette compagnie."
        : "Impossible de charger les appareils."))
      .finally(() => setLoading(false));
  }, []);

  const chargerUsername = useCallback((username) => {
    const u = (username || "").trim();
    if (!u) { setDevices([]); return; }
    setLoading(true);
    axiosInstance
      .get(`/api/device/user/${encodeURIComponent(u)}`)
      .then((res) => setDevices(Array.isArray(res.data) ? res.data : []))
      .catch((e) => message.error(e?.response?.status === 403
        ? "Cet utilisateur n'appartient pas à votre périmètre."
        : "Impossible de charger les appareils."))
      .finally(() => setLoading(false));
  }, []);

  const chargerPending = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get("/api/device/pending")
      .then((res) => setDevices(Array.isArray(res.data) ? res.data : []))
      .catch(() => message.error("Impossible de charger la file à valider."))
      .finally(() => setLoading(false));
  }, []);

  const rafraichir = useCallback(() => {
    if (mode === "pending") chargerPending();
    else if (mode === "username") chargerUsername(usernameSearch);
    else chargerOrganisme(organismeCode);
  }, [mode, organismeCode, usernameSearch, chargerOrganisme, chargerUsername, chargerPending]);

  useEffect(() => {
    if (!hasHAC113) return;
    if (mode === "organisme" && organismeCode) chargerOrganisme(organismeCode);
    else if (mode === "pending") chargerPending();
  }, [mode, organismeCode, chargerOrganisme, chargerPending]);

  const revoquer = (deviceId) => {
    axiosInstance
      .delete(`/api/device/admin/${deviceId}`)
      .then(() => { message.success("Appareil révoqué."); rafraichir(); })
      .catch(() => message.error("Échec de la révocation."));
  };

  const approuver = (deviceId) => {
    axiosInstance
      .post(`/api/device/approve/${deviceId}`)
      .then(() => { message.success("Appareil approuvé — l'utilisateur pourra se connecter."); rafraichir(); })
      .catch(() => message.error("Échec de l'approbation."));
  };

  const iconePour = (os) => (os && ["iOS", "Android"].includes(os) ? <MobileOutlined /> : <LaptopOutlined />);

  const devicesFiltres = useMemo(() => {
    const f = filtre.trim().toLowerCase();
    if (!f) return devices;
    return devices.filter((d) =>
      [d.username, d.label, d.browser, d.os, d.ipLast]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(f)));
  }, [devices, filtre]);

  const columns = [
    {
      title: "Utilisateur",
      dataIndex: "username",
      key: "username",
      width: 200,
      fixed: "left",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Appareil",
      key: "appareil",
      render: (_, d) => (
        <Space>
          <span style={{ fontSize: 18, color: brand.primary }}>{iconePour(d.os)}</span>
          <div>
            <div style={{ fontWeight: 600 }}>
              {d.label || `${d.browser || "Navigateur"}${d.os ? " · " + d.os : ""}`}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {(d.browser || "—")}{d.os ? " · " + d.os : ""}{d.ipLast ? " · " + d.ipLast : ""}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Statut",
      key: "statut",
      width: 160,
      render: (_, d) =>
        d.revoked ? <Tag color="red">Révoqué</Tag>
          : d.trusted ? <Tag color="green" icon={<SafetyCertificateOutlined />}>De confiance</Tag>
            : d.approvalPending ? <Tag color="gold">⏳ À valider</Tag>
              : <Tag color="orange">Non de confiance</Tag>,
    },
    {
      title: "Dernière activité",
      dataIndex: "lastSeenAt",
      key: "lastSeenAt",
      width: 160,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"),
    },
    {
      title: "Ajouté le",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
    },
    {
      title: "Actions",
      key: "action",
      width: 170,
      render: (_, d) => (
        <Space>
          {!d.trusted && !d.revoked && (
            <Popconfirm
              title="Approuver cet appareil ?"
              description="Vérifiez l'identité de l'utilisateur. Il pourra ensuite se connecter sans code depuis cet appareil."
              okText="Approuver" cancelText="Annuler"
              onConfirm={() => approuver(d.deviceId)}
            >
              <Tooltip title="Approuver (valider la connexion)">
                <Button size="small" type="primary" icon={<CheckOutlined />}>Approuver</Button>
              </Tooltip>
            </Popconfirm>
          )}
          {!d.revoked && (
            <Popconfirm
              title="Révoquer cet appareil ?"
              description="L'utilisateur devra repasser une vérification à sa prochaine connexion."
              okText="Révoquer" okType="danger" cancelText="Annuler"
              onConfirm={() => revoquer(d.deviceId)}
            >
              <Tooltip title="Révoquer">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar" style={{ background: brand.soft }}>
      <div className="layout-container">
        <SideBar />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h4 className="fw-bold py-3 mb-4" style={{ color: brand.primary }}>
                <span className="text-muted fw-light">Sécurité /</span> Appareils des utilisateurs
              </h4>

              {!hasHAC113 ? (
                <div className="card" style={{ borderRadius: 12 }}>
                  <div className="card-body">
                    <Empty description="Accès non autorisé — habilitation HAC113 requise." />
                  </div>
                </div>
              ) : (
                <div className="card" style={{ borderRadius: 12 }}>
                  <div className="card-body">
                    <Space wrap style={{ marginBottom: 16 }}>
                      <Segmented
                        value={mode}
                        onChange={setMode}
                        options={[
                          { label: "⏳ À valider", value: "pending" },
                          { label: "Par compagnie", value: "organisme" },
                          { label: "Par utilisateur", value: "username" },
                        ]}
                      />
                      {mode === "organisme" && isPool && (
                        <Select
                          style={{ minWidth: 260 }}
                          placeholder="Choisir une compagnie"
                          value={organismeCode}
                          onChange={setOrganismeCode}
                          showSearch
                          optionFilterProp="label"
                          options={organismes.map((o) => ({
                            label: o.raisonSociale || o.code, value: o.code,
                          }))}
                        />
                      )}
                      {mode === "organisme" && !isPool && (
                        <Tag color="blue">{LOGGED_USER?.profil?.organisme?.raisonSociale}</Tag>
                      )}
                      {mode === "username" && (
                        <Input.Search
                          style={{ width: 320 }}
                          placeholder="Nom d'utilisateur (e-mail de connexion)"
                          enterButton={<SearchOutlined />}
                          value={usernameSearch}
                          onChange={(e) => setUsernameSearch(e.target.value)}
                          onSearch={chargerUsername}
                        />
                      )}
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={rafraichir}
                      >
                        Actualiser
                      </Button>
                      <Input
                        allowClear
                        style={{ width: 240 }}
                        placeholder="Filtrer (utilisateur, IP, navigateur…)"
                        prefix={<SearchOutlined />}
                        value={filtre}
                        onChange={(e) => setFiltre(e.target.value)}
                      />
                    </Space>

                    <Table
                      rowKey="deviceId"
                      size="small"
                      columns={columns}
                      dataSource={devicesFiltres}
                      loading={loading}
                      pagination={{ pageSize: 20, showSizeChanger: true }}
                      scroll={{ x: 1000 }}
                    />
                  </div>
                </div>
              )}
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAppareils;
